import * as React from 'react';
import {
  buildBackgroundImageUrl,
  computeBackgroundOffset,
  loadBackgroundBreakpoints,
  pickBreakpoint,
} from '../../utils/backgroundBreakpoints';
import { loadImageDimensions } from '../../utils/backgroundImage';

type Rect = { x: number; y: number; width: number; height: number };

type BackgroundLayerProps = {
  optionIdentifier: string | null;
  detailImageUrl: string | null;
  designWidthMm: number;
  /** World-space rect the backdrop must cover. This is the fit-to-viewport rect (not the raw
   *  canvas), so the wall fills the visible area edge-to-edge with no letterbox stripes, while
   *  still being a fixed rect in design space that zooms/pans with the design. */
  fillRect: Rect;
  /** Reports whether the background image is currently loading (drives a screen-space spinner). */
  onLoadingChange?: (loading: boolean) => void;
};

type BackgroundLayout = {
  url: string;
  x: number;
  y: number;
  width: number;
  height: number;
};

function fillRectLayout(url: string, rect: Rect): BackgroundLayout {
  return { url, x: rect.x, y: rect.y, width: rect.width, height: rect.height };
}

export default function BackgroundLayer({
  optionIdentifier,
  detailImageUrl,
  designWidthMm,
  fillRect,
  onLoadingChange,
}: BackgroundLayerProps) {
  const [layout, setLayout] = React.useState<BackgroundLayout | null>(null);
  const [imageLoaded, setImageLoaded] = React.useState(false);

  // Track when the resolved image is actually decoded. A plain <image onLoad> is
  // unreliable for cached images (the breakpoint path preloads them), so drive the
  // fade-in from an Image() preloader and the `complete` flag for the cached case.
  React.useEffect(() => {
    setImageLoaded(false);
    if (!layout?.url) return undefined;

    let cancelled = false;
    const done = () => { if (!cancelled) setImageLoaded(true); };
    const img = new Image();
    img.onload = done;
    img.onerror = done; // stop the spinner even if it fails — the grey rect remains
    img.src = layout.url;
    if (img.complete) done();

    return () => { cancelled = true; };
  }, [layout?.url]);

  React.useEffect(() => {
    if (!optionIdentifier) {
      setLayout(null);
      return;
    }

    let cancelled = false;

    const applyDetailImageLayout = () => {
      if (cancelled || !detailImageUrl) {
        if (!cancelled) setLayout(null);
        return;
      }
      setLayout(fillRectLayout(detailImageUrl, fillRect));
    };

    (async () => {
      try {
        const breakpoints = await loadBackgroundBreakpoints(optionIdentifier);
        const bp = pickBreakpoint(breakpoints, designWidthMm || 500);

        if (bp) {
          const breakpointUrl = buildBackgroundImageUrl(optionIdentifier, bp);
          try {
            const { width, height } = await loadImageDimensions(breakpointUrl);
            if (cancelled) return;
            setLayout({
              url: breakpointUrl,
              ...computeBackgroundOffset(bp, width, height),
            });
            return;
          } catch {
            // Fall through to detailImage when breakpoint asset is missing or fails to load.
          }
        }

        applyDetailImageLayout();
      } catch {
        applyDetailImageLayout();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [optionIdentifier, detailImageUrl, designWidthMm, fillRect.x, fillRect.y, fillRect.width, fillRect.height]);

  // Loading while a background is selected but its image isn't ready/painted yet.
  // Reported up so a screen-space spinner can render over the visible canvas.
  const isLoading = Boolean(optionIdentifier) && (!layout || !imageLoaded);

  React.useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  React.useEffect(() => () => onLoadingChange?.(false), [onLoadingChange]);

  return (
    <g pointerEvents="none">
      <rect x={fillRect.x} y={fillRect.y} width={fillRect.width} height={fillRect.height} fill="#f5f5f5" />
      {layout && (
        <image
          href={layout.url}
          x={layout.x}
          y={layout.y}
          width={layout.width}
          height={layout.height}
          preserveAspectRatio="xMidYMid slice"
          opacity={imageLoaded ? 1 : 0}
          style={{ transition: 'opacity 0.3s ease' }}
        />
      )}
    </g>
  );
}
